import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupabaseAuth, useAccess } from '../context/AuthContext';
import { getDisplayName } from '../lib/userPreferences';
import useMyTasks from '../hooks/useMyTasks';
import SettingsModal from './SettingsModal';
import TaskInboxPanel from './TaskInboxPanel';

function initials(name, email) {
  const src = (name || email || '?').trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function shortName(profile, email) {
  const display = getDisplayName(profile);
  if (display && display !== email?.split('@')[0]) return display;
  if (!profile?.full_name) return email?.split('@')[0] || 'User';
  const parts = profile.full_name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0]} ${parts[parts.length - 1][0]}.`;
  return profile.full_name;
}

const ROLE_LABEL = {
  admin: 'Administrator',
  programme_manager: 'Programme manager',
  project_manager: 'Project manager',
  member: 'Member',
  viewer: 'Viewer',
};

function UserAvatar({ name, email, openCount, overdueCount }) {
  return (
    <div className="user-avatar-wrap">
      <div className="user-avatar">{initials(name, email)}</div>
      {openCount > 0 && (
        <span
          className={`task-badge${overdueCount > 0 ? ' overdue' : ''}`}
          title={overdueCount > 0 ? `${openCount} open (${overdueCount} overdue)` : `${openCount} open tasks`}
        >
          {openCount}
        </span>
      )}
    </div>
  );
}

function AccountFlyout({
  open,
  name,
  email,
  isAdmin,
  isPlatformStaff,
  tasks,
  tasksLoading,
  onTaskNavigate,
  onSettings,
  onAdmin,
  onPlatform,
  onSignOut,
  memberships,
  currentOrgId,
  onSwitchOrg,
}) {
  if (!open) return null;
  const showOrgSwitcher = Array.isArray(memberships) && memberships.length > 1;
  return (
    <div className="user-dd user-flyout open">
      <div className="user-dd-top">
        <div className="user-dd-name">{name || 'User'}</div>
        {email && <div className="user-dd-email">{email}</div>}
      </div>
      {showOrgSwitcher && (
        <>
          <div className="user-dd-divider" />
          <div className="user-dd-label">Switch organisation</div>
          {memberships.map((m) => (
            <button
              key={m.organisation_id}
              type="button"
              className={`user-dd-item${m.organisation_id === currentOrgId ? ' on' : ''}`}
              onClick={() => onSwitchOrg?.(m.organisation_id)}
            >
              {m.organisations?.name || 'Organisation'}
              {m.organisation_id === currentOrgId && <span className="user-dd-check">✓</span>}
            </button>
          ))}
        </>
      )}
      <TaskInboxPanel
        tasks={tasks}
        loading={tasksLoading}
        onNavigate={onTaskNavigate}
      />
      <div className="user-dd-divider" />
      <button type="button" className="user-dd-item" onClick={onSettings}>
        Settings
      </button>
      {isAdmin && (
        <button type="button" className="user-dd-item" onClick={onAdmin}>
          Admin
        </button>
      )}
      {isPlatformStaff && (
        <button type="button" className="user-dd-item" onClick={onPlatform}>
          Platform console
        </button>
      )}
      <div className="user-dd-divider" />
      <button type="button" className="user-dd-item user-dd-danger" onClick={onSignOut}>
        Sign out
      </button>
    </div>
  );
}

export default function UserChip({ collapsed = false }) {
  const navigate = useNavigate();
  const { profile, user, signOut, memberships, switchOrganisation } = useSupabaseAuth();
  const { isAdmin } = useAccess();
  const { tasks, loading: tasksLoading, openCount, overdueCount, refresh } = useMyTasks(profile?.id);
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const ref = useRef(null);

  const name = profile?.full_name || '';
  const displayLabel = getDisplayName(profile);
  const email = profile?.email || user?.email || '';
  const roleKey = profile?.platform_role === 'consultant' ? 'member' : (profile?.platform_role || 'member');

  useEffect(() => {
    if (!open) return;
    refresh();
  }, [open, refresh]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  function openSettings() {
    setOpen(false);
    setSettingsOpen(true);
  }

  function openAdmin() {
    setOpen(false);
    navigate('/admin');
  }

  function openPlatform() {
    setOpen(false);
    navigate('/platform');
  }

  function handleSignOut() {
    setOpen(false);
    void signOut();
  }

  function closeFlyout() {
    setOpen(false);
  }

  if (collapsed) {
    return (
      <>
        <div className="sb-foot sb-foot-mini" ref={ref}>
          <button
            type="button"
            className={`user-avatar-btn${open ? ' open' : ''}`}
            title={displayLabel || email}
            onClick={() => setOpen((o) => !o)}
          >
            <UserAvatar
              name={name}
              email={email}
              openCount={openCount}
              overdueCount={overdueCount}
            />
          </button>
          <AccountFlyout
            open={open}
            name={name}
            email={email}
            isAdmin={isAdmin}
            isPlatformStaff={Boolean(profile?.is_platform_staff)}
            tasks={tasks}
            tasksLoading={tasksLoading}
            onTaskNavigate={closeFlyout}
            onSettings={openSettings}
            onAdmin={openAdmin}
            onPlatform={openPlatform}
            onSignOut={handleSignOut}
            memberships={memberships}
            currentOrgId={profile?.organisation_id}
            onSwitchOrg={switchOrganisation}
          />
        </div>
        <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      </>
    );
  }

  return (
    <>
      <div className={`sb-foot${open ? ' flyout-open' : ''}`} ref={ref}>
        <button type="button" className={`user-row${open ? ' open' : ''}`} onClick={() => setOpen((o) => !o)}>
          <UserAvatar
            name={name}
            email={email}
            openCount={openCount}
            overdueCount={overdueCount}
          />
          <div className="user-info">
            <div className="user-name">{shortName(profile, email)}</div>
            <div className="user-role">{ROLE_LABEL[roleKey] || roleKey}</div>
          </div>
          <span className="user-row-arr" aria-hidden>▾</span>
        </button>
        <AccountFlyout
          open={open}
          name={name}
          email={email}
          isAdmin={isAdmin}
          isPlatformStaff={Boolean(profile?.is_platform_staff)}
          tasks={tasks}
          tasksLoading={tasksLoading}
          onTaskNavigate={closeFlyout}
          onSettings={openSettings}
          onAdmin={openAdmin}
          onPlatform={openPlatform}
          onSignOut={handleSignOut}
          memberships={memberships}
          currentOrgId={profile?.organisation_id}
          onSwitchOrg={switchOrganisation}
        />
      </div>
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
}
